import React, { useState } from 'react';
import { EM_TONE } from '../../email/mockData';
import type { EmThread } from '../../email/types';
import type { IconKind } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Pill } from '../Pill/Pill';

const panelEyebrow: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
};

function EHead({ icon, color, label, right }: { icon: IconKind; color?: string; label: string; right?: React.ReactNode }) {
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--cream-2)', display: 'flex', alignItems: 'center', gap: 7 }}>
      <Icon kind={icon} size={13} stroke={1.9} style={{ color: color || 'var(--ink-3)' }} />
      <span style={{ ...panelEyebrow, fontSize: 9.5 }}>{label}</span>
      {right ? <span style={{ marginLeft: 'auto' }}>{right}</span> : null}
    </div>
  );
}

function ELine({ icon, color, label, value }: { icon: IconKind; color: string; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink-2)' }}>
      <Icon kind={icon} size={12} style={{ color }} />
      <span style={{ color: 'var(--ink-3)' }}>{label}:</span>
      <span style={{ fontWeight: 600, color: 'var(--ink-1)' }}>{value}</span>
    </div>
  );
}

function EmConsent({ label, granted }: { label: string; granted: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--ink-2)' }}>
      <Icon kind={granted ? 'shield-check' : 'lock'} size={13} style={{ color: granted ? 'var(--success)' : 'var(--terracotta)' }} />
      <span>{label} consent:</span>
      <span style={{ fontWeight: 600, color: granted ? 'var(--success)' : 'var(--terracotta)' }}>{granted ? 'granted' : 'not granted'}</span>
    </div>
  );
}

function EmChartNote({ thread }: { thread: EmThread }) {
  const [note, setNote] = useState(`Email thread · ${thread.name}\n${thread.summary}`);
  return (
    <div className="em-card">
      <EHead icon="clipboard" color="var(--gold)" label="Chart note draft" right={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-4)' }}>auto</span>} />
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{
          width: '100%',
          minHeight: 92,
          padding: '11px 14px',
          border: 0,
          outline: 'none',
          background: 'transparent',
          resize: 'vertical',
          fontFamily: 'var(--font-mono)',
          fontSize: 11.5,
          color: 'var(--ink-1)',
          lineHeight: 1.55,
          boxSizing: 'border-box',
        }}
      />
      <div
        style={{
          padding: '9px 12px',
          borderTop: '1px solid var(--cream-2)',
          background: 'var(--cream-0)',
          display: 'flex',
          gap: 7,
          alignItems: 'center',
        }}
      >
        <button type="button" className="em-chip" style={{ fontSize: 11, padding: '4px 10px' }}>
          <Icon kind="copy" size={11} /> Copy
        </button>
        <Button variant="primary" size="sm" icon="external" style={{ marginLeft: 'auto', height: 28 }}>
          Push to PMS
        </Button>
      </div>
    </div>
  );
}

function ActionsTab({ thread }: { thread: EmThread }) {
  const p = thread.patient;
  const atts = thread.messages.flatMap((m) => m.attachments || []);

  return (
    <div style={{ padding: '14px 16px 26px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {thread.triage ? (
        <div
          className="em-card"
          style={{
            padding: '12px 14px',
            background: 'var(--terracotta-tint)',
            borderColor: 'color-mix(in srgb, var(--terracotta) 26%, transparent)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'var(--cream-1)',
                color: 'var(--terracotta)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon kind="alert" size={15} stroke={2} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: 'var(--terracotta)',
                    background: 'var(--cream-1)',
                    padding: '1px 7px',
                    borderRadius: 5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {thread.triage.id}
                </span>
                <span style={{ ...panelEyebrow, fontSize: 9, color: 'var(--terracotta)' }}>In Triage Inbox</span>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-0)', marginTop: 4 }}>{thread.triage.title}</div>
            </div>
          </div>
          <button
            type="button"
            className="em-chip"
            style={{
              width: '100%',
              justifyContent: 'center',
              marginTop: 10,
              padding: '6px 10px',
              color: 'var(--terracotta)',
              borderColor: 'color-mix(in srgb, var(--terracotta) 30%, transparent)',
              background: 'var(--cream-1)',
            }}
          >
            Open in Triage <Icon kind="external" size={11} />
          </button>
        </div>
      ) : null}

      <div className="em-card" style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 11 }}>
          <span
            className="ms-av ms-av-patient"
            style={{
              width: 38,
              height: 38,
              fontSize: 12.5,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
            }}
          >
            {p.initials}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-0)' }}>{p.name}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 1 }}>
              {p.age ? `${p.gender}, ${p.age} · ` : ''}
              <span style={{ fontFamily: 'var(--font-mono)' }}>{p.patientId || p.status}</span>
            </div>
          </div>
          <Pill tone={p.status === 'New patient' ? 'ai' : p.status === 'Vendor' ? 'neutral' : 'success'}>{p.status}</Pill>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5 }}>
          <ELine icon="calendar" color="var(--success)" label="Next" value={p.next ? `${p.next.date} · ${p.next.time}` : 'None scheduled'} />
          <ELine icon="shield" color="var(--gold)" label="Insurance" value={p.insurance.plan} />
          <ELine icon="dollar" color="var(--success)" label="Balance" value={p.balance} />
        </div>
      </div>

      <div className="em-card">
        <EHead icon="sparkle" color="var(--ai-3)" label="Live summary" right={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-4)' }}>{thread.when}</span>} />
        <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--ink-1)', lineHeight: 1.6 }}>{thread.summary}</div>
      </div>

      {thread.suggested.length > 0 ? (
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 14.5, fontWeight: 600, color: 'var(--ink-0)' }}>Suggested actions</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, marginBottom: 10 }}>Elva can run these — approve or skip</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {thread.suggested.map((s, i) => {
              const tn = EM_TONE[s.tone];
              return (
                <div key={i} className="em-card" style={{ padding: 13 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 9 }}>
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        background: tn.bg,
                        color: tn.fg,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon kind={s.icon} size={14} stroke={1.9} />
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)' }}>{s.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 3, lineHeight: 1.5 }}>{s.detail}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{s.sub}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 9, borderTop: '1px solid var(--cream-2)' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '2px 9px',
                        borderRadius: 'var(--r-sm)',
                        fontSize: 10.5,
                        fontWeight: 700,
                        background: s.risk === 'med' ? 'var(--gold-tint)' : 'var(--success-tint)',
                        color: s.risk === 'med' ? 'var(--gold)' : 'var(--success)',
                      }}
                    >
                      {s.risk === 'med' ? 'Medium' : 'Low risk'}
                    </span>
                    <button type="button" className="em-chip" style={{ fontSize: 11, padding: '3px 10px' }}>
                      Skip
                    </button>
                    <Button variant="primary" size="sm" iconLeft="check" style={{ marginLeft: 'auto', height: 28 }}>
                      Run
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <EmChartNote thread={thread} />

      <div className="em-card" style={{ padding: 14 }}>
        <div style={{ ...panelEyebrow, marginBottom: 9, fontSize: 9.5 }}>Consent</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <EmConsent label="Email" granted={p.consent.email} />
          <EmConsent label="SMS" granted={p.consent.sms} />
        </div>
      </div>

      {atts.length > 0 ? (
        <div className="em-card">
          <EHead icon="image" color="var(--ai-3)" label={`Attachments · ${atts.length}`} />
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {atts.map((a, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 'var(--r-sm)',
                  border: '1px solid var(--cream-2)',
                  background: 'var(--cream-0)',
                }}
              >
                <span
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--r-sm)',
                    background: 'var(--surface-mute)',
                    border: '1px solid var(--cream-2)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                  }}
                >
                  <Icon kind="image" size={18} style={{ color: 'var(--ai-3)' }} />
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--ink-4)', marginTop: 1 }}>{a.size}</div>
                </div>
                <button type="button" className="em-chip" style={{ fontSize: 11, padding: '5px 9px' }}>
                  <Icon kind="download" size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export interface EmailRightPanelProps {
  thread: EmThread;
  open: boolean;
  onClose: () => void;
}

export function EmailRightPanel({ thread, open, onClose }: EmailRightPanelProps) {
  const n = thread.suggested.length + (thread.triage ? 1 : 0);
  if (!open) return null;

  return (
    <>
      <div className="em-intel-scrim" onClick={onClose} aria-hidden />
      <aside className="em-drawer">
        <div style={{ height: 3, background: 'var(--grad-spectrum)', flex: 'none' }} />
        <div className="em-drawer-head">
          <span className="em-drawer-icon">
            <Icon kind="sparkle" size={16} stroke={2} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.015em' }}>AI intelligence</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{thread.name}</div>
          </div>
          <IconButton icon="x" size={32} title="Close" onClick={onClose} />
        </div>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--cream-2)', padding: '0 6px', flex: 'none' }}>
          <button type="button" className="em-tab active">
            Actions{' '}
            {n > 0 ? (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, color: 'var(--ai-3)', background: 'var(--ai-tint)', padding: '1px 6px', borderRadius: 5 }}>{n}</span>
            ) : null}
          </button>
        </div>
        <div className="cal-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <ActionsTab thread={thread} />
        </div>
      </aside>
    </>
  );
}
